/**
 * Owner-admin API client (consumed by /admin/* pages).
 *
 * Talks to two backend surfaces:
 *   /api/admin/        — older is_staff-gated endpoints (user list, mutations)
 *   /api/admin/v2/     — newer is_superuser-only endpoints (marketplace, finances)
 *
 * Wrappers exist so component code stays free of axios noise + URL strings.
 */
import api from "./axios";

// ─── Users (existing /api/admin/) ───────────────────────────────────────

export const listUsers = (params = {}) =>
  api.get("/api/admin/users/", { params });

export const getUser = (id) =>
  api.get(`/api/admin/users/${id}/`);

export const setUserActive = (id, is_active) =>
  api.post(`/api/admin/users/${id}/set-active/`, { is_active });

export const resetUserPassword = (id) =>
  api.post(`/api/admin/users/${id}/reset-password/`);

// Soft delete — non-destructive tombstone. All PII (canonical_email,
// phone, phone_verified_at, password hash) preserved. Revocable via
// revokeUserDelete below. Cashfree mandate is cancelled so the user
// isn't billed while tombstoned.
// 200 -> {message: "soft_deleted", revocable: true}
// 400 -> superuser / self
export const deleteUser = (id) =>
  api.delete(`/api/admin/users/${id}/delete/`);

// Revoke a soft delete. is_active flipped back on, deleted_at cleared.
// Cashfree mandate is NOT resurrected — user must re-subscribe if they
// want a paid plan.
// 200 -> {message: "revoked"}
// 400 -> {error: "not_soft_deleted"} if user isn't soft-deleted
export const revokeUserDelete = (id) =>
  api.post(`/api/admin/users/${id}/revoke-delete/`);

// Hard delete — permanent DB removal. Refused if user has wallet
// transactions, invoices, or coded matches. Requires typed "HARD DELETE"
// confirmation in the body.
// 200 -> {message: "hard_deleted", reversible: false}
// 409 -> {error: "has_data", blockers: ["wallet transactions", ...]}
// 400 -> confirm mismatch / superuser / self
export const hardDeleteUser = (id) =>
  api.delete(`/api/admin/users/${id}/hard-delete/`, {
    data: { confirm: "HARD DELETE" },
  });

// 2026-06-11 — manual Users-tab classification override. Empty string
// clears it (back to derived/"auto"). Valid: "", "B2C", "B2B", "PLAYER".
export const setUserTabOverride = (id, override) =>
  api.post(`/api/admin/users/${id}/tab-override/`, { override });

// ─── Finances (new /api/admin/v2/) ─────────────────────────────────────

export const getFinancesSummary = (range = "mtd") =>
  api.get("/api/admin/v2/finances/summary/", { params: { range } });

export const listUserFinancials = (params = {}) =>
  api.get("/api/admin/v2/finances/users/", { params });

export const getUserFinancials = (userId, range = "mtd") =>
  api.get(`/api/admin/v2/finances/users/${userId}/`, { params: { range } });

// 2026-06-18 — Unified billing feed (Invoice + topup + sub-grant + credit
// note) for the /admin/billing tab. Single GET, all filters as params.
export const listBillingLines = (params = {}) =>
  api.get("/api/admin/v2/billing/lines/", { params });

// 2026-06-29 — AWS Cost Explorer rollup for /admin/spend. Backend caches
// 10 min ($0.01/CE request), so refreshes are cheap.
export const getAwsSpend = () =>
  api.get("/api/admin/v2/finances/aws-spend/");

// 2026-10-06 — Everything the account runs (servers, DB, ball data,
// buckets, videos per tournament). 202 {building, stale} while the
// server builds it in the background — poll without `refresh`.
export const getAwsInventory = ({ refresh = false } = {}) =>
  api.get("/api/admin/v2/finances/aws-inventory/", {
    params: refresh ? { refresh: 1 } : {},
  });


// ─── Owner Console notifications (new-signups bell) ────────────────────

export const listSignupNotifications = (params = {}) =>
  api.get("/api/admin/v2/notifications/signups/", { params });

export const getSignupUnreadCount = () =>
  api.get("/api/admin/v2/notifications/signups/unread-count/");

export const markSignupsSeen = (at = null) =>
  api.post("/api/admin/v2/notifications/signups/mark-seen/", at ? { at } : {});

// ─── Marketplace packs (new /api/admin/v2/) ────────────────────────────

export const listPacks = (params = {}) =>
  api.get("/api/admin/v2/marketplace/packs/", { params });

export const getPack = (id) =>
  api.get(`/api/admin/v2/marketplace/packs/${id}/`);

export const createPack = (data) =>
  api.post("/api/admin/v2/marketplace/packs/", data);

export const updatePack = (id, data) =>
  api.patch(`/api/admin/v2/marketplace/packs/${id}/`, data);

export const deletePack = (id) =>
  api.delete(`/api/admin/v2/marketplace/packs/${id}/`);

// Cascade-refund delete — refunds every paid PackPurchase, drops all
// PackPurchase rows, then deletes the Pack. Use this whenever the
// pack has buyers; plain `deletePack` will 409 because PackPurchase
// has PROTECT on its FK.
export const deletePackWithRefunds = (id) =>
  api.post(`/api/admin/v2/marketplace/packs/${id}/delete-with-refunds/`);

// Deliver what was added to a pack since each buyer bought it (a purchase
// is a one-time copy). GET = dry-run preview, POST = apply. No credits
// move; nothing is deleted or overwritten. See marketplace/services_sync.py.
export const previewPackBuyerSync = (id) =>
  api.get(`/api/admin/v2/marketplace/packs/${id}/sync-buyers/`);
export const applyPackBuyerSync = (id) =>
  api.post(`/api/admin/v2/marketplace/packs/${id}/sync-buyers/`);

export const publishPack = (id, { skipPromotion = false } = {}) =>
  api.post(`/api/admin/v2/marketplace/packs/${id}/publish/`,
    skipPromotion ? { skip_promotion: true } : {});

export const unpublishPack = (id) =>
  api.post(`/api/admin/v2/marketplace/packs/${id}/unpublish/`);

// SQUAD_PACK CSV upload. Posts a CSV of team rosters; backend parses
// into PackTeam + PackPlayer rows under the pack. Returns a summary
// { teams_created, players_created, errors[], teams[{name, player_count}] }.
export const uploadSquadCsv = (packId, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post(
    `/api/admin/v2/marketplace/packs/${packId}/upload-squad-csv/`, fd,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
};

// OFFICIALS_PACK CSV / XLSX upload. Posts a list of umpires + referees;
// backend parses into PackUmpire rows. Summary shape:
// { umpires_created, umpire_count, referee_count, errors[] }.
export const uploadOfficialsCsv = (packId, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post(
    `/api/admin/v2/marketplace/packs/${packId}/upload-officials-csv/`, fd,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
};

// VENUE_PACK CSV / XLSX upload. Posts a list of stadiums; backend
// parses into PackVenue rows. Summary shape:
// { venues_created, errors[], venues[{name, city, country}] }.
export const uploadVenuesCsv = (packId, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post(
    `/api/admin/v2/marketplace/packs/${packId}/upload-venues-csv/`, fd,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
};

// 2026-07-18 — SYSTEM-catalog picker feeds for the marketplace pack
// composer wizards. Replace the CSV-upload flow with "pick already-
// ingested players / teams / umpires / stadiums from the catalog".
// All four return DRF page shape: { count, next, previous, results }.
export const listCatalogPlayers = (params = {}) =>
  api.get("/api/admin/v2/marketplace/catalog/players/", { params });
export const listCatalogTeams = (params = {}) =>
  api.get("/api/admin/v2/marketplace/catalog/teams/", { params });
export const listCatalogUmpires = (params = {}) =>
  api.get("/api/admin/v2/marketplace/catalog/umpires/", { params });
export const listCatalogVenues = (params = {}) =>
  api.get("/api/admin/v2/marketplace/catalog/venues/", { params });

// 2026-07-18 — write the picker's selection into PackTeam / PackPlayer /
// PackUmpire / PackVenue templates. Idempotent at pack level (backend
// wipes prior template rows before the new selection is written).
// Squad body: { teams: [{ team_id, player_ids: [] }, ...] }
export const selectSquadFromCatalog = (packId, teams) =>
  api.post(
    `/api/admin/v2/marketplace/packs/${packId}/select-squad/`,
    { teams },
  );
export const selectOfficialsFromCatalog = (packId, umpireIds) =>
  api.post(
    `/api/admin/v2/marketplace/packs/${packId}/select-officials/`,
    { umpire_ids: umpireIds },
  );
export const selectVenuesFromCatalog = (packId, venueIds) =>
  api.post(
    `/api/admin/v2/marketplace/packs/${packId}/select-venues/`,
    { venue_ids: venueIds },
  );

// 2026-07-31 — TOURNAMENT_BUNDLE composer. Body shape:
//   { items: [{ child_id, position }, ...] }
// Backend wipes prior PackBundleItem rows for this bundle before
// writing, so re-selection is idempotent (parity with the sibling
// select-squad / select-officials / select-venues contract).
export const selectBundleItems = (packId, items) =>
  api.post(
    `/api/admin/v2/marketplace/packs/${packId}/select-bundle-items/`,
    { items },
  );

// 2026-06-08 — admin match export. UserDrawer uses these to list a
// user's matches and download each as an .xlsx workbook.
export const listAdminUserMatches = (userId) =>
  api.get(`/api/admin/v2/users/${userId}/matches/`);

// 2026-06-23 — global admin matches list. Returns every coded match
// (owner_type=USER) across all users, paginated, with search / date /
// format / owner_email filters. Used by /admin/matches once drilled
// into a specific user folder.
export const listAdminAllMatches = (params = {}) =>
  api.get(`/api/admin/v2/matches/all/`, { params });

// 2026-06-23 — per-user folder list for /admin/matches. One row per
// user who has coded at least one match. Powers the default view
// (folders) before the operator drills into a specific user.
export const listAdminMatchOwners = (params = {}) =>
  api.get(`/api/admin/v2/matches/owners/`, { params });

// 2026-06-23 — superuser-only hard delete from /admin/matches. Cascades
// all Innings / Ball / wicket / video rows tied to the match.
export const deleteAdminMatch = (matchId) =>
  api.delete(`/api/admin/v2/matches/${matchId}/`);

export const downloadAdminMatchXlsx = async (matchId) => {
  const r = await api.get(
    `/api/admin/v2/matches/${matchId}/export-xlsx/`,
    { responseType: "blob" },
  );
  // Pull the filename from Content-Disposition so the file lands on
  // disk as ZCD_<team1>_vs_<team2>_<date>_<short>.xlsx rather than the
  // raw URL path.
  const cd = r.headers?.["content-disposition"] || "";
  const m = /filename="?([^"]+)"?/.exec(cd);
  const filename = (m && m[1]) || `ZCD_Match_${String(matchId).slice(0, 8)}.xlsx`;
  const url = URL.createObjectURL(r.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return { filename };
};

/* ─── Bulk data export (System → Data export) ──────────────────────────
 *
 * Converted ball-by-ball data for a whole competition. The backend
 * streams it, so the browser starts receiving bytes long before the
 * server has finished producing them — do NOT switch this to a JSON
 * fetch that materialises the season in memory on either side.
 */

export const listDataExportCompetitions = () =>
  api.get("/api/admin/v2/data-export/competitions/");

// Same auth-then-blob dance as downloadAdminMatchXlsx above: the JWT
// lives in the axios default headers, so a bare <a href> would 403.
export const downloadCompetitionBallsCsv = async (competitionId) => {
  const r = await api.get(
    `/api/admin/v2/data-export/competitions/${competitionId}/balls.csv`,
    { responseType: "blob" },
  );
  const cd = r.headers?.["content-disposition"] || "";
  const m = /filename="?([^"]+)"?/.exec(cd);
  const filename = (m && m[1]) || `zcd-balls-${String(competitionId).slice(0, 8)}.csv`;
  const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return { filename };
};

export const listPackPlayers = (packId) =>
  api.get(`/api/admin/v2/marketplace/packs/${packId}/players/`);

export const createPackPlayer = (packId, data) =>
  api.post(`/api/admin/v2/marketplace/packs/${packId}/players/`, data);

export const updatePackPlayer = (packId, id, data) =>
  api.patch(`/api/admin/v2/marketplace/packs/${packId}/players/${id}/`, data);

export const deletePackPlayer = (packId, id) =>
  api.delete(`/api/admin/v2/marketplace/packs/${packId}/players/${id}/`);

export const listPackTeams = (packId) =>
  api.get(`/api/admin/v2/marketplace/packs/${packId}/teams/`);

export const createPackTeam = (packId, data) =>
  api.post(`/api/admin/v2/marketplace/packs/${packId}/teams/`, data);

export const updatePackTeam = (packId, id, data) =>
  api.patch(`/api/admin/v2/marketplace/packs/${packId}/teams/${id}/`, data);

export const deletePackTeam = (packId, id) =>
  api.delete(`/api/admin/v2/marketplace/packs/${packId}/teams/${id}/`);

// ─── Marketplace promotion audit (Activity tab) ────────────────────────

export const listMarketplacePromotions = (params = {}) =>
  api.get("/api/admin/v2/marketplace/promotions/", { params });

// ─── ZenAdminAudit feed (Activity tab) ─────────────────────────────────

export const listAdminAudit = (params = {}) =>
  api.get("/api/admin/v2/activity/audit/", { params });

// CSV download. Fetches with auth (the JWT lives in the axios default
// headers, so a plain <a href> wouldn't carry it), then synthesises a
// Blob URL and triggers the browser download. Resolves once the user's
// download has started.
export const downloadAdminAuditCsv = async (params = {}) => {
  const res = await api.get("/api/admin/v2/activity/audit/", {
    params: { ...params, format: "csv" },
    responseType: "blob",
  });
  const blob = new Blob([res.data], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  // Pull filename out of Content-Disposition if the server set one;
  // backend writes `zen-admin-audit-YYYYMMDD-HHMMSS.csv` by default.
  const disposition = res.headers?.["content-disposition"] || "";
  const m = /filename="?([^"]+)"?/.exec(disposition);
  const filename = m ? m[1] : `zen-admin-audit-${Date.now()}.csv`;
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke later so the browser has time to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// ─── Scorer marketplace moderation ─────────────────────────────────────

export const listScorerProfiles = (params = {}) =>
  api.get("/api/admin/v2/scorer-profiles/", { params });

export const getScorerProfile = (id) =>
  api.get(`/api/admin/v2/scorer-profiles/${id}/`);

export const approveScorerProfile = (id) =>
  api.post(`/api/admin/v2/scorer-profiles/${id}/approve/`);

export const denyScorerProfile = (id, { internalNote = "", customerFacingReason = "" } = {}) =>
  api.post(`/api/admin/v2/scorer-profiles/${id}/deny/`, {
    internal_note: internalNote,
    customer_facing_reason: customerFacingReason,
  });

export const withdrawScorerProfile = (id, reason = "") =>
  api.post(`/api/admin/v2/scorer-profiles/${id}/withdraw/`, { reason });

export const issueScorerBadge = (id) =>
  api.post(`/api/admin/v2/scorer-profiles/${id}/issue-badge/`);

export const revokeScorerBadge = (id, reason = "") =>
  api.post(`/api/admin/v2/scorer-profiles/${id}/revoke-badge/`, { reason });

// ─── Dashboard summary (one-shot consolidated payload) ─────────────────

export const getDashboardSummary = () =>
  api.get("/api/admin/v2/dashboard/summary/");

// ─── Organizations (B2B admin) ─────────────────────────────────────────

export const listOrganizations = (params = {}) =>
  api.get("/api/admin/v2/organizations/", { params });

export const getOrganization = (id) =>
  api.get(`/api/admin/v2/organizations/${id}/`);

export const updateOrganization = (id, data) =>
  api.patch(`/api/admin/v2/organizations/${id}/`, data);

export const suspendOrganization = (id) =>
  api.post(`/api/admin/v2/organizations/${id}/suspend/`);

export const reactivateOrganization = (id) =>
  api.post(`/api/admin/v2/organizations/${id}/reactivate/`);

// ─── Analyzer access grants (per-user, per-tournament) ─────────────────

export const listAnalyzerGrants = (params = {}) =>
  api.get("/api/admin/v2/analyzer-grants/", { params });

export const getAnalyzerGrant = (id) =>
  api.get(`/api/admin/v2/analyzer-grants/${id}/`);

export const createAnalyzerGrant = ({ userId, competitionIds, notes = "" }) =>
  api.post("/api/admin/v2/analyzer-grants/", {
    user_id: userId,
    competition_ids: competitionIds,
    notes,
  });

export const updateAnalyzerGrant = (id, { competitionIds, notes, isActive }) => {
  const body = {};
  if (competitionIds !== undefined) body.competition_ids = competitionIds;
  if (notes !== undefined) body.notes = notes;
  if (isActive !== undefined) body.is_active = isActive;
  return api.patch(`/api/admin/v2/analyzer-grants/${id}/`, body);
};

export const revokeAnalyzerGrant = (id) =>
  api.post(`/api/admin/v2/analyzer-grants/${id}/revoke/`);

export const restoreAnalyzerGrant = (id) =>
  api.post(`/api/admin/v2/analyzer-grants/${id}/restore/`);

export const searchAnalyzerGrantUsers = (q, type) =>
  api.get("/api/admin/v2/analyzer-grants/users/", {
    params: { q, ...(type ? { type } : {}) },
  });

// Mint a new analyst login from the Access console. Body is
// {email, full_name?, username?, password?}; backend returns
// `{user, plaintext_password}` — the password is shown ONCE, copy it
// before closing the toast.
export const createAnalyzerUser = ({ email, fullName, username, password } = {}) =>
  api.post("/api/admin/v2/analyzer-grants/users/create/", {
    email,
    ...(fullName ? { full_name: fullName } : {}),
    ...(username ? { username } : {}),
    ...(password ? { password } : {}),
  });

export const listAnalyzerGrantCompetitions = (search = "") =>
  api.get("/api/admin/v2/analyzer-grants/competitions/", { params: { search } });

// ─── Live-Auction access grants (admin shares an auction with a user) ──

export const listLiveAuctionTemplates = (search = "") =>
  api.get("/api/admin/v2/live-auction-templates/", { params: { search } });

export const listLiveAuctionGrants = (params = {}) =>
  api.get("/api/admin/v2/live-auction-grants/", { params });

export const createLiveAuctionGrant = ({ templateAuctionId, userId, notes = "" }) =>
  api.post("/api/admin/v2/live-auction-grants/", {
    template_auction_id: templateAuctionId,
    user_id: userId,
    notes,
  });

export const deleteLiveAuctionGrant = (id) =>
  api.delete(`/api/admin/v2/live-auction-grants/${id}/`);

export const searchLiveAuctionGrantUsers = (search = "", type = "", template = "") =>
  api.get("/api/admin/v2/live-auction-grants/users/", {
    params: {
      search,
      ...(type ? { type } : {}),
      ...(template ? { template } : {}),
    },
  });

// ─── Pre-Auction access grants (admin shares a Pre-Auction with a user) ──

export const toggleOrgArchiveAccess = (id) =>
  api.post(`/api/admin/v2/organizations/${id}/toggle_archive/`);

export const toggleOrgAnalyzerAccess = (id) =>
  api.post(`/api/admin/v2/organizations/${id}/toggle_analyzer/`);

export const markOrgVerified = (id) =>
  api.post(`/api/admin/v2/organizations/${id}/mark_verified/`);

export const unverifyOrg = (id) =>
  api.post(`/api/admin/v2/organizations/${id}/unverify/`);

export const approveOrgDocument = (orgId, docId, reviewerNote = "") =>
  api.post(`/api/admin/v2/organizations/${orgId}/documents/${docId}/approve/`,
    reviewerNote ? { reviewer_note: reviewerNote } : {});

export const rejectOrgDocument = (orgId, docId, reviewerNote = "") =>
  api.post(`/api/admin/v2/organizations/${orgId}/documents/${docId}/reject/`,
    reviewerNote ? { reviewer_note: reviewerNote } : {});

export const updateOrgSubscriptionOverride = (orgId, { customPriceInr, contractNotes }) =>
  api.post(`/api/admin/v2/organizations/${orgId}/subscription/override/`, {
    custom_price_inr: customPriceInr,
    contract_notes: contractNotes,
  });

export const issueOrgInvoice = (orgId, { taxableInr, periodStart, periodEnd, lineDescription }) =>
  api.post(`/api/admin/v2/organizations/${orgId}/invoices/issue/`, {
    taxable_inr: taxableInr,
    period_start: periodStart || null,
    period_end: periodEnd || null,
    line_description: lineDescription || "",
  });

// ─── Team catalog (2026-07-31) ─────────────────────────────────────────
// The only surface that can rename an ingested team: the customer-facing
// /api/coredata/teams/ PATCH refuses SYSTEM / locked rows outright.
// Defaults server-side to owner_type=SYSTEM + archival=false.
// params: {search, owner_type, archival, team_type, renamed, page, page_size}

export const listAdminTeams = (params = {}) =>
  api.get("/api/admin/v2/teams/", { params });

export const getAdminTeam = (teamId) =>
  api.get(`/api/admin/v2/teams/${teamId}/`);

// Renaming appends a TeamNameHistory row (year defaults to the current
// year when omitted). 409 => a team with that name already exists in the
// same tenant; body carries conflict_team_id.
export const updateAdminTeam = (teamId, { name, shortName, changeYear, changeReason }) =>
  api.patch(`/api/admin/v2/teams/${teamId}/`, {
    ...(name !== undefined ? { name } : {}),
    ...(shortName !== undefined ? { short_name: shortName } : {}),
    ...(changeYear ? { name_change_year: changeYear } : {}),
    ...(changeReason ? { name_change_reason: changeReason } : {}),
  });

// ── Venue Sync (2026-10-12) ────────────────────────────────────────
// Join archive (BBB-ingest) venues to catalog venues. GET returns the
// pending archive venues with ranked suggestions, the catalog, countries
// and recent actions. Commit is all-or-nothing; each action is undoable.
export const getVenueSync = () => api.get("/api/admin/v2/venue-sync/");

// links: [{archival_id, catalog_id}]
// promotes: [{archival_id, name, city, nationality_id}]
export const commitVenueSync = ({ links = [], promotes = [] }) =>
  api.post("/api/admin/v2/venue-sync/commit/", { links, promotes });

export const undoVenueSync = (logId) =>
  api.post(`/api/admin/v2/venue-sync/undo/${logId}/`);

// ─── Refunds (2026-09-16) ──────────────────────────────────────────────
// Console equivalent of Django admin's RefundRequest screen. Amounts
// cross the wire as RUPEES (amount_inr); the backend converts to paise
// with Decimal. Never send floats-as-paise from here.
//
// candidates = captured payments with how much of each is still
// refundable, so the operator picks a payment instead of typing a number.

export const listRefundCandidates = (params = {}) =>
  api.get("/api/admin/v2/refunds/candidates/", { params });

export const listRefunds = (params = {}) =>
  api.get("/api/admin/v2/refunds/", { params });

export const getRefund = (id) =>
  api.get(`/api/admin/v2/refunds/${id}/`);

export const createRefund = (data) =>
  api.post("/api/admin/v2/refunds/", data);

// action: "approve" | "deny" | "process" | "approve-process"
// 409 => state machine refused (row moved / double click)
// 422 => approved but no gateway target could be resolved
// 503 => provider transient; safe to retry
export const refundAction = (id, action, data = {}) =>
  api.post(`/api/admin/v2/refunds/${id}/${action}/`, data);

// Analyzer chat (Ask) spend per account -- Access console "Chatbot usage".
// period: "month" | "30d" | "all" | "YYYY-MM"
export const getAskUsage = (period = "month") =>
  api.get("/api/admin/v2/ask-usage/", { params: { period } });

export const getAskUsageUser = (userId, period = "month") =>
  api.get(`/api/admin/v2/ask-usage/${userId}/`, { params: { period } });
